import { localizeReportValue } from "./reportValueCatalog";
import { localizeReportNarrative } from "./reportNarrativeCatalog";

/**
 * Presentation-only verified localization for Sheet13 B34.
 *
 * Authoritative canonical formula:
 * 当前最优发展方向：${currentGoal}；首选行业：${top1Industry}；首选岗位：${top1Role}
 *
 * Rules:
 * - Never parses the canonical string.
 * - Never mutates or recomputes the production result.
 * - English rendering is permitted only when the supplied canonical B34
 *   byte-exactly matches the authoritative template rebuilt from the
 *   separately supplied canonical components.
 * - Any missing/unknown/mismatched component returns the canonical value.
 */
export function presentSheet13SummaryAdvice(
  locale,
  canonical,
  {
    currentGoal,
    top1Industry,
    top1Role,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    typeof currentGoal !== "string" ||
    typeof top1Industry !== "string" ||
    typeof top1Role !== "string"
  ) {
    return canonical;
  }

  const expectedCanonical =
    `当前最优发展方向：${currentGoal}；首选行业：${top1Industry}；首选岗位：${top1Role}`;

  if (canonical !== expectedCanonical) return canonical;

  const goal = localizeReportValue("en", "currentGoal", currentGoal);
  const industry = localizeReportValue("en", "industry", top1Industry);
  const role = localizeReportValue("en", "role", top1Role);

  // Fail closed to canonical if any finite canonical component is not covered.
  if (
    goal === currentGoal ||
    industry === top1Industry ||
    role === top1Role
  ) {
    return canonical;
  }

  return `Current best development direction: ${goal}; preferred industry: ${industry}; preferred role: ${role}`;
}

const SHEET15_SUPPORT_DIRECTION_EN = Object.freeze({
  "补木": "Wood-supporting priorities",
  "补火": "Fire-supporting priorities",
  "补土": "Earth-supporting priorities",
  "补金": "Metal-supporting priorities",
  "补水": "Water-supporting priorities",
});

/**
 * I18N-C3-B2 — presentation-only verified localization for Sheet15 B23.
 *
 * Authoritative canonical formula:
 * 结合当前目标「${currentGoal}」：若目标为事业/创业，优先选择能承接${fiveElementNeed}能量的地区、行业和岗位；
 * 若目标为财富，则优先考虑现金流、制度和长期积累，不以方位作为唯一决策依据。
 *
 * B37 and B39 are workbook aliases of B23 and therefore use this same
 * presenter when needed by a presentation surface.
 *
 * Rules:
 * - Never parses/splits/replaces the canonical string.
 * - Never recomputes Sheet15.
 * - Rebuilds the authoritative canonical template only from separately
 *   supplied canonical components and requires byte-exact equality.
 * - fiveElementNeed === "" is a VALID semantic value.
 * - Missing, unknown or mismatched components fail closed to canonical.
 * - No geographic interpretation or regional location mapping.
 */
export function presentSheet15WealthAdvice(
  locale,
  canonical,
  {
    currentGoal,
    fiveElementNeed,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  if (
    typeof currentGoal !== "string" ||
    typeof fiveElementNeed !== "string"
  ) {
    return canonical;
  }

  const expectedCanonical =
    `结合当前目标「${currentGoal}」：若目标为事业/创业，优先选择能承接${fiveElementNeed}能量的地区、行业和岗位；若目标为财富，则优先考虑现金流、制度和长期积累，不以方位作为唯一决策依据。`;

  if (canonical !== expectedCanonical) return canonical;

  const goal = localizeReportValue(
    "en",
    "currentGoal",
    currentGoal,
  );

  if (goal === currentGoal) return canonical;

  if (fiveElementNeed === "") {
    return (
      `Considering the current goal “${goal}”: ` +
      "for career or entrepreneurship goals, prioritize suitable regions, industries, and roles; " +
      "for wealth goals, prioritize cash flow, systems, and long-term accumulation. " +
      "Direction should not be used as the sole basis for decisions."
    );
  }

  const supportDirection =
    SHEET15_SUPPORT_DIRECTION_EN[fiveElementNeed];

  if (supportDirection === undefined) return canonical;

  return (
    `Considering the current goal “${goal}”: ` +
    `for career or entrepreneurship goals, prioritize regions, industries, and roles aligned with ${supportDirection}; ` +
    "for wealth goals, prioritize cash flow, systems, and long-term accumulation. " +
    "Direction should not be used as the sole basis for decisions."
  );
}

/**
 * I18N-C3-B3 — presentation-only verified localization for Sheet15 B40.
 *
 * Authoritative canonical formula:
 * 八字依据：日主${dayMasterElement}，日主状态${dayMasterStatus}，五行需求${fiveElementNeed}。
 * 系统建议优先考虑${topDevelopmentDirection}，次选${secondaryDevelopmentDirection}；
 * 行业以${recommendedIndustries}为主，岗位以${recommendedRoles}为主。
 * 应减少${reduceDevelopmentDirection}或${unsuitableEnvironment}。
 * 此结论来自八字五行需求与日主强弱的规则化判断，属于传统文化参考，
 * 不作为职业、搬迁或投资决策唯一依据。
 *
 * Rules:
 * - Never parses/splits/replaces the canonical B40 string.
 * - Never recomputes Sheet15.
 * - Rebuilds authoritative B40 only from separately supplied canonical slots.
 * - Requires byte-exact equality before English presentation.
 * - fiveElementNeed === "" is a valid semantic value.
 * - "—" is a valid authoritative sentinel and is preserved.
 * - Any missing, unknown, or mismatched required component fails closed.
 * - No geographic interpretation or regional location mapping.
 */
export function presentSheet15IntegratedConclusion(
  locale,
  canonical,
  {
    dayMasterElement,
    dayMasterStatus,
    fiveElementNeed,
    topDevelopmentDirection,
    secondaryDevelopmentDirection,
    recommendedIndustries,
    recommendedRoles,
    reduceDevelopmentDirection,
    unsuitableEnvironment,
  } = {},
) {
  if (typeof canonical !== "string") return canonical;
  if (locale !== "en") return canonical;

  const requiredComponents = [
    dayMasterElement,
    dayMasterStatus,
    fiveElementNeed,
    topDevelopmentDirection,
    secondaryDevelopmentDirection,
    recommendedIndustries,
    recommendedRoles,
    reduceDevelopmentDirection,
    unsuitableEnvironment,
  ];

  if (requiredComponents.some((value) => typeof value !== "string")) {
    return canonical;
  }

  const expectedCanonical =
    `八字依据：日主${dayMasterElement}，日主状态${dayMasterStatus}，五行需求${fiveElementNeed}。` +
    `系统建议优先考虑${topDevelopmentDirection}，次选${secondaryDevelopmentDirection}；` +
    `行业以${recommendedIndustries}为主，岗位以${recommendedRoles}为主。` +
    `应减少${reduceDevelopmentDirection}或${unsuitableEnvironment}。` +
    `此结论来自八字五行需求与日主强弱的规则化判断，属于传统文化参考，不作为职业、搬迁或投资决策唯一依据。`;

  if (canonical !== expectedCanonical) return canonical;

  const element = localizeReportValue(
    "en",
    "element",
    dayMasterElement,
  );

  const status = localizeReportValue(
    "en",
    "dayMasterStatus",
    dayMasterStatus,
  );

  if (
    element === dayMasterElement ||
    status === dayMasterStatus
  ) {
    return canonical;
  }

  let need;

  if (fiveElementNeed === "") {
    need = "No specific support element";
  } else {
    need = localizeReportValue(
      "en",
      "fiveElementNeed",
      fiveElementNeed,
    );

    if (need === fiveElementNeed) return canonical;
  }

  const localizeSheet15Required = (value) => {
    if (value === "—") return value;

    const localized = localizeReportNarrative(
      "en",
      "sheet15",
      value,
    );

    return localized === value ? undefined : localized;
  };

  const topDirection =
    localizeSheet15Required(topDevelopmentDirection);

  const secondaryDirection =
    localizeSheet15Required(secondaryDevelopmentDirection);

  const industries =
    localizeSheet15Required(recommendedIndustries);

  const roles =
    localizeSheet15Required(recommendedRoles);

  const reduceDirection =
    localizeSheet15Required(reduceDevelopmentDirection);

  const avoidEnvironment =
    localizeSheet15Required(unsuitableEnvironment);

  if (
    topDirection === undefined ||
    secondaryDirection === undefined ||
    industries === undefined ||
    roles === undefined ||
    reduceDirection === undefined ||
    avoidEnvironment === undefined
  ) {
    return canonical;
  }

  return (
    `BaZi basis: Day Master ${element}; Day Master status: ${status}; Five-Element support need: ${need}. ` +
    `The system recommends prioritizing ${topDirection}, with ${secondaryDirection} as the secondary direction; ` +
    `recommended industries: ${industries}; recommended roles: ${roles}. ` +
    `Reduce emphasis on ${reduceDirection} or ${avoidEnvironment}. ` +
    "This conclusion is based on rule-based interpretation of Five-Element needs and Day Master strength. " +
    "It is provided as a traditional-cultural reference and should not be used as the sole basis for career, relocation, or investment decisions."
  );
}
